import { Component, Input, OnChanges } from '@angular/core';
import { Product } from '../../../model/product/product.model';

let _lpUid = 0;

/**
 * A LINE profile avatar asset (i_20 person / i_21 group): the CLASSIC default
 * avatar, nothing more, a CIRCLE (transparent corners) holding a vertical
 * token gradient (windowBackground up top into editorBackground below) and a
 * circle-BLUE silhouette: one figure for the person, the classic depth pair
 * (front-left big, behind-right small, background-coloured gap between) for
 * the group.
 */
@Component( {
  selector: 'app-line-profile',
  templateUrl: './line-profile.component.html',
  styleUrls: [ './line-profile.component.scss' ],
} )
export class LineProfileComponent implements OnChanges {

  @Input() product!: Product;

  /** Avatar kind: a single person (i_20) or a group (i_21). */
  @Input() kind: 'person' | 'group' = 'person';

  /** Background gradient: windowBackground (top) -> editorBackground (bottom). */
  bgTop = '#123243';
  bgBot = '#142B37';

  /** Figure colour: the circle blue (circle 2) for both avatar kinds. */
  fig = '#549EFF';

  /** Unique suffix so each instance's gradient id can't collide. */
  readonly uid = 'lp-' + ( _lpUid++ );

  ngOnChanges(): void {
    const p = this.product;
    if ( !p ) {
      return;
    }
    const t: any = p.designTokens || {};
    const cp = ( p.compactPalette?.length ? p.compactPalette : p.palette ) || [];
    const eb = t.editorBackground || t.windowBackground || '#142B37';
    const wb = t.windowBackground || eb;
    // Everest (owner 2026-07-17): a soft sunrise-wash avatar — the background
    // circle is a gentle #FDF3F0 -> slightly-lighter gradient, and the figure
    // takes the menu-buttons ON colour (sunrise orange) instead of the blue
    // colour-circle, so the profile matches the tab bar's active tint.
    if ( p.key === 'everest' ) {
      this.bgTop = '#FEF9F8';
      this.bgBot = '#FDF3F0';
      this.fig = '#ED7E5A';
    } else if ( p.key === 'sakura' ) {
      // Sakura (owner 2026-07-24): the rose colour-circle (#CB6B91) read too
      // dark for the figure — use the soft cherry-blossom pink on a gentle
      // pink wash so the avatar stays light and on-theme.
      this.bgTop = '#FEF6FA';
      this.bgBot = '#FCEDF3';
      this.fig = '#E898B4';
    } else if ( p.key === 'tokyo' ) {
      // Tokyo (owner 2026-08-05): the profile takes LINE's own plum family, like
      // the on/off tab buttons — a DARK plum avatar wash (it's a dark theme) with
      // the bright violet figure that matches the tab's ON colour.
      // Profile is decoupled from the tab buttons (owner 2026-08-05): its wash +
      // figure sit 10° further from blue than the periwinkle buttons, so keep
      // these independent of the menu-button colour.
      this.bgTop = '#4C3E68';                           // plum wash (10° off the button hue)
      this.bgBot = '#3C304F';
      this.fig = '#D4C8FF';
    } else if ( p.key === 'roraima' ) {
      // Roraima (owner 2026-08-13): "profile iconları da roraima sarısı olacak,
      // arka planı da sarının koyusu" — the figure takes the theme's Dry Grass
      // yellow, the same one the passcode tumbleweeds are made of, and the wash
      // is that yellow taken right down rather than the theme's own near-black.
      // So the avatar is one hue at two ends of its range, which is why the
      // figure reads without a hard edge against the disc.
      this.bgTop = '#5A4718';                           // deep ochre, the yellow taken down
      this.bgBot = '#3D300F';
      this.fig = '#DDBE6D';                             // Dry Grass, the theme's yellow
    } else if ( p.key === 'salda' ) {
      // Salda: the generic fallback picks colour circle 2, which on this theme is
      // OATMEAL — a pale sand figure on a pale sand wash, i.e. an avatar that is
      // not there. This is the first LIGHT theme whose own palette is pale at
      // BOTH ends, and the fallback has no way to know that.
      //
      // BEIGE, not the lake (owner 2026-08-15). The wash and the figure both come
      // off the shore now rather than the water: a warm cream disc with a sand
      // brown figure, 3.05:1 on the wash's deeper end.
      //
      // This breaks the avatar-matches-the-tab-bar convention the other themes
      // here follow, and does it knowingly. Salda's sampled skin puts turquoise on
      // the header AND the passcode keypad, so a teal avatar was the third
      // turquoise disc on a screen whose subject is a white shore.
      this.bgTop = '#FBF2E4';
      this.bgBot = '#EFE0C8';
      this.fig = '#9C7A4E';
    } else if ( p.key === 'fuji' ) {
      // Fuji: the blush at two ends of its own range with the figure in the tab
      // bar's vermilion. The generic fallback would take colour circle 2 — the
      // dawn rose — which on a blush wash is Salda's failure again.
      // LINE PINK on a pale wash (owner 2026-08-17: the figure was reading dark).
      // The vermilion this replaced measured 4.61 on the wash, which is a strong
      // silhouette and on this skin the wrong one — every other surface here is
      // pink and a rust-red person was the only hard mark on the screen.
      //
      // 2.53:1, which is low by this family's habits and deliberately so: an avatar
      // is not a control and does not have to shout. The wash was also lightened at
      // the top so the pink has somewhere to sit.
      this.bgTop = '#FFF6F3';
      this.bgBot = '#FFF0EA';
      this.fig = '#FC6A77';
    } else if ( p.key === 'etna' ) {
      // Etna: the sampled near-black at two ends of its own range, with the figure
      // in the LAVA — the tab bar's own colour, which is the rule Sydney settled.
      //
      // It was the ember sand #E2BF79, chosen as "the one warm mark outside the
      // images". On the sampled skin that reasoning is gone: the chrome carries no
      // warmth at all now, so the avatar has no quota to spend and can simply match
      // the bar. 3.94:1 on the wash.
      this.bgTop = '#242424';
      this.bgBot = '#141414';
      this.fig = '#D74C57';
    } else if ( p.key === 'madrid' ) {
      // Madrid: the sampled saffron at two ends of its own range with the figure
      // in the tab bar's flamenco red, so the avatar matches the tab bar the way
      // Everest's does. The wash moved from cream to yellow with the skin; the
      // figure did not have to, which is the one value on this theme the sample
      // agreed with.
      // ALL YELLOW, and no red anywhere (owner 2026-08-16, in two steps: first the
      // wash went red, then "profile resimleri sarı tonları olsun, kırmızıları
      // kaldır").
      //
      // The complaint the red was meant to answer was that the avatars read as too
      // yellow — and the answer turned out not to be a different hue but more
      // SEPARATION inside the one it already had. The wash is pale butter into
      // saffron and the figure is a deep amber: 5.75:1, where the red-on-saffron
      // version it started from measured 2.09 and was the actual problem.
      this.bgTop = '#FFEFB8';
      this.bgBot = '#FFE79C';
      this.fig = '#8A6A12';   // 4.13:1 — lightened from 5.75, which read as brown
    } else if ( p.key === 'vienna' ) {
      // Vienna: the sampled blush at two ends of its own range with the figure in
      // the header's own vermilion — the rule Sydney settled, where the silhouette
      // takes the colour of the bar at the top of the screen so the avatar belongs
      // to the same app as the header.
      //
      // Inverted from the wine wash with a cream figure this replaced. On a pale
      // ground a pale figure is a hole, which is the same inversion the passcode
      // ramp and the tab pair both had to make.
      this.bgTop = '#FBE7E0';
      this.bgBot = '#F2D6CB';
      this.fig = '#EE4333';
    } else if ( p.key === 'spring' ) {
      // Spring: the meadow at two ends of its own range with the figure in the
      // deeper green. NOT the tab bar's rose here, and it is the one exception to
      // the rule the other themes in this run follow: an avatar is a person, and a
      // rose figure on a green wash reads as the hummingbird rather than as one.
      this.bgTop = '#DFF2D6';
      this.bgBot = '#C8EAB9';
      this.fig = '#3C8F63';
    } else if ( p.key === 'reynisfjara' ) {
      // Reynisfjara: the theme's dark at two ends of its range with the figure in
      // the tab bar's driftwood — the one warm mark this theme allows itself.
      this.bgTop = '#2A3138';
      this.bgBot = '#151A20';
      this.fig = '#C5A98F';
    } else if ( p.key === 'sydney' ) {
      // Sydney: the harbour at two ends of its own range, with the figure in the
      // SKIN'S OWN HEADER BLUE (owner 2026-08-15) rather than the tab bar's deeper
      // one. The header is the largest block of colour the phone shows and it is
      // sampled, so an avatar cut from it belongs to the skin rather than to a
      // value we chose. The generic fallback would take colour circle 2 —
      // #73B9D0, a mid harbour on a pale harbour wash, which is Salda's failure
      // again.
      this.bgTop = '#D8EDF3';
      this.bgBot = '#B4DAE6';
      this.fig = '#0D8DC3';
    } else if ( p.key === 'prague' ) {
      // Prague: the night at two ends of its own range with the figure in the
      // tab bar's candle gold — Roraima's shape, where the wash is deep enough
      // that the figure needs no edge drawn around it.
      this.bgTop = '#123151';
      this.bgBot = '#0A1826';
      this.fig = '#E3CA77';
    } else if ( p.key === 'ocean' ) {
      // Ocean: the SHALLOWS at two ends of their own range with the figure in the
      // tab bar's active green, so the avatar matches the tab bar the way Sydney's
      // and Salda's do. It was the reverse — deep water washed under a bright mint
      // figure — which is the dark-skin shape, and on this body it would have been
      // the only near-black disc on a near-white screen.
      this.bgTop = '#D9F3EF';
      this.bgBot = '#AEE4DC';
      // The figure follows the tab bar's active green wherever that lands, and it
      // landed four steps lighter than the #0E5248 this started on (owner
      // 2026-08-15: the silhouettes were too dark). It moves WITH lineTabOn every
      // time that moves — the owner's own rule is that the Home glyph, the
      // passcode dots and this silhouette sit at one lightness.
      this.fig = '#1B8570';
    } else {
      this.bgTop = wb;
      this.bgBot = eb;
      this.fig = cp[ 1 ] || t.primaryColor || '#549EFF';
    }
  }
}
